import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Upload, X, Plus, FileText, Image, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

const AddSupportTicket = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [issueTypeCategories, setIssueTypeCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    issue_name: '',
    client_id: '',
    issue_type: '',
    issue_description: '',
    issue_link: '',
    issue_device: '',
    documents: [],
    issue_steps: [''],  // Changed to array with one initial step
    priority: 'Medium'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchClients();
    fetchIssueTypeCategories();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchIssueTypeCategories = async () => {
    try {
      const response = await axios.get(`${API}/issue-type-categories`);
      setIssueTypeCategories(response.data);
      
      // Set first category as default if available
      if (response.data.length > 0 && !formData.issue_type) {
        setFormData(prev => ({ ...prev, issue_type: response.data[0].name }));
      }
    } catch (error) {
      console.error('Error fetching issue type categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle steps array changes
  const handleStepChange = (index, value) => {
    const newSteps = [...formData.issue_steps];
    newSteps[index] = value;
    setFormData(prev => ({
      ...prev,
      issue_steps: newSteps
    }));
    
    // Clear error when user starts typing
    if (errors.issue_steps) {
      setErrors(prev => ({
        ...prev,
        issue_steps: ''
      }));
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      issue_steps: [...prev.issue_steps, '']
    }));
  };

  const removeStep = (index) => {
    if (formData.issue_steps.length > 1) {
      const newSteps = formData.issue_steps.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        issue_steps: newSteps
      }));
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        uploadedFiles.push({
          name: file.name,
          url: response.data.url,
          type: file.type,
          size: file.size
        });
      }
      
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (type.startsWith('video/')) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.issue_name.trim()) {
      newErrors.issue_name = 'Issue name is required';
    }
    
    if (!formData.client_id) {
      newErrors.client_id = 'Please select a client';
    }
    
    if (!formData.issue_description.trim()) {
      newErrors.issue_description = 'Issue description is required';
    }
    
    if (!formData.issue_steps.some(step => step.trim())) {
      newErrors.issue_steps = 'At least one issue step is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        documents: formData.documents.map(doc => doc.url),
        issue_steps: formData.issue_steps.filter(step => step.trim()) // Remove empty steps
      };
      
      const response = await axios.post(`${API}/support-tickets`, submitData);
      
      alert('Support ticket created successfully!');
      navigate('/support/tickets');
    } catch (error) {
      console.error('Error creating support ticket:', error);
      alert('Failed to create support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/support/tickets')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Add Support Ticket</h1>
          <p className="text-gray-600 mt-1">Create a new support ticket for issue tracking</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
        {/* Issue Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Issue Name *
          </label>
          <input
            type="text"
            name="issue_name"
            value={formData.issue_name}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.issue_name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Brief description of the issue"
          />
          {errors.issue_name && (
            <p className="text-red-500 text-sm mt-1">{errors.issue_name}</p>
          )}
        </div>

        {/* Client and Issue Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Name *
            </label>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.client_id ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.company_name}
                </option>
              ))}
            </select>
            {errors.client_id && (
              <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Type
            </label>
            <select
              name="issue_type"
              value={formData.issue_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {issueTypeCategories.length === 0 ? (
                <option value="">No issue types available</option>
              ) : (
                issueTypeCategories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Issue Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Issue Description *
          </label>
          <textarea
            name="issue_description"
            value={formData.issue_description}
            onChange={handleInputChange}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.issue_description ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Detailed description of the issue"
          />
          {errors.issue_description && (
            <p className="text-red-500 text-sm mt-1">{errors.issue_description}</p>
          )}
        </div>

        {/* Issue Link and Device */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Link
            </label>
            <input
              type="url"
              name="issue_link"
              value={formData.issue_link}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.com/issue-page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Device
            </label>
            <input
              type="text"
              name="issue_device"
              value={formData.issue_device}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Desktop Chrome, iPhone Safari"
            />
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority
          </label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>

        {/* Documents Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Documents
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="document-upload"
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
            />
            <label
              htmlFor="document-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                Click to upload documents, images, or videos
              </span>
              <span className="text-xs text-gray-400 mt-1">
                Supports: Images, Videos, PDF, DOC, TXT
              </span>
            </label>
          </div>

          {/* Uploaded Documents */}
          {formData.documents.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Documents:</h4>
              {formData.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getFileIcon(doc.type)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocument(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="mt-2 text-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-1">Uploading files...</p>
            </div>
          )}
        </div>

        {/* Steps to Replicate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Steps to Replicate *
          </label>
          <div className={`border rounded-lg p-4 ${errors.issue_steps ? 'border-red-300' : 'border-gray-300'}`}>
            {formData.issue_steps.map((step, index) => (
              <div key={index} className="flex items-start space-x-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mt-1">
                  {index + 1}
                </div>
                <div className="flex-1 flex items-start space-x-2">
                  <input
                    type="text"
                    value={step}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Step ${index + 1}`}
                  />
                  {formData.issue_steps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove Step"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addStep}
              className="mt-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add More Steps</span>
            </button>
          </div>
          {errors.issue_steps && (
            <p className="text-red-500 text-sm mt-1">{errors.issue_steps}</p>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate('/support/tickets')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSupportTicket;